import {Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {DragulaService} from 'ng2-dragula';

import {DesignService} from '@services/left-menu/design/design.service';

import {PageItem} from '@models/sidebar/page-item';
import {PageTypeOption} from '@models/sidebar/response/page-type-option';
import {MenuItemType} from '@models/sidebar/menu-item-type';

@Component({
    selector: 'scitis-list-menu-items',
    templateUrl: './list-menu-items.component.html',
    styleUrls: ['./list-menu-items.component.scss'],
    standalone: false
})
export class ListMenuItemsComponent implements OnDestroy, OnChanges {


  @Input()
  baseMenuItems: PageItem[] = [];

  @Input()
  userMenuItems: PageItem[] = [];

  @Input()
  pageTypeOptions: PageTypeOption[] = [];

  @Input()
  pageItemsToBeDeleted: PageItem[] = [];

  @Input()
  dragAndDropEnabledForBaseMenuItems = false;

  @Input()
  isAdminOrHigher: boolean = false;

  @Input()
  isHigherThanAdmin: boolean = false;

  @Input()
  menuItemType: MenuItemType | string = '';

  @Input()
  refreshNodes: string[] = [];

  @Input()
  addFormIsActive: boolean = false;

  @Input()
  allPagesInsideNode: PageItem[] = [];

  @Output()
  pageItemDelete: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  pageItemEdit: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  nodeItemEdit: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  baseMenuItemsOrderChanged: EventEmitter<PageItem[]> = new EventEmitter<PageItem[]>();

  @Output()
  userMenuItemsOrderChanged: EventEmitter<PageItem[]> = new EventEmitter<PageItem[]>();

  @Output()
  sharePageItem: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  sharePageItemInsideNode: EventEmitter<Map<string, PageItem>> = new EventEmitter<Map<string, PageItem>>();

  @Output()
  editPageItemInsideNode: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  propertiesFallback: EventEmitter<Object> = new EventEmitter<Object>();

  @Output()
  unsharePageItem: EventEmitter<PageItem> = new EventEmitter<PageItem>();

  @Output()
  updatedItemsInsideNode: EventEmitter<Map<string, PageItem[]>> = new EventEmitter<Map<string, PageItem[]>>();

  @Output()
  disableSaveButton: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  pageFormIsActive: EventEmitter<any> = new EventEmitter<any>();

  @Output()
  pagesInsideNode: EventEmitter<PageItem[]> = new EventEmitter<PageItem[]>();

  dragAndDropUserMenuItemsInProgress: boolean = false;
  itemsInsideNodeMap: Map<string, PageItem[]> = new Map<string, PageItem[]>();
  itemsInsideNode: PageItem[] = [];
  dragAndDropBaseMenuItems = 'BASE_MENU_ITEMS';
  dragAndDropUserMenuItems = 'USER_MENU_ITEMS';
  subscription = new Subscription();

  constructor(
      private dragulaService: DragulaService,
      private designService: DesignService) {
    this.useDragula();
  }

  get showUserMenuItems(): boolean {
    return this.menuItemType === MenuItemType.MACHINE;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.addFormIsActive && this.addFormIsActive) {
      this.onDeactivatePageForms(null);
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.dragulaService.destroy(this.dragAndDropBaseMenuItems);
    this.dragulaService.destroy(this.dragAndDropUserMenuItems);
  }

  @HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
    this.dragulaService.destroy(this.dragAndDropBaseMenuItems);
    this.dragulaService.destroy(this.dragAndDropUserMenuItems);
  }

  nodeClass(pageItem: PageItem): string {
    return this.designService.getNodeClass(pageItem.type);
  }

  onDeletePageItem(pageItem: PageItem): void {
    this.pageItemDelete.emit(pageItem);
  }

  onEditPageItem(pageItem: PageItem): void {
    this.pageItemEdit.emit(pageItem);
  }

  setPropertiesFallback(propertiesFallback): void {
    this.propertiesFallback.emit(propertiesFallback);
  }

  onEditNodeItem(pageItem: PageItem): void {
    this.nodeItemEdit.emit(pageItem);
  }

  onSharePageItem(pageItem: PageItem): void {
    this.sharePageItem.emit(pageItem);
  }

  onSharePageItemInsideNode(pageItem: Map<string, PageItem>): void {
    this.sharePageItemInsideNode.emit(pageItem);
  }

  onEditPageItemInsideNode(pageItem: PageItem): void {
    this.editPageItemInsideNode.emit(pageItem);
  }

  onUnsharePageItem(pageItem: PageItem): void {
    this.unsharePageItem.emit(pageItem);
  }

  onUpdateUserMenuItemsAfterNodeDelete(pageItems: PageItem[]): void {
    for (const pageItem of pageItems) {
      const pageIsNotMarkedToBeDeleted = this.pageItemsToBeDeleted.filter(m => m.id === pageItem.id).length === 0;
      if (pageIsNotMarkedToBeDeleted) {
        this.userMenuItems.push(pageItem);
      }
    }
  }

  onDisableSaveButton(disable: boolean): void {
    this.disableSaveButton.emit(disable);
  }

  onDeactivateForms(pageItem: PageItem | null): void {
    this.pageFormIsActive.emit();
    this.onDeactivatePageForms(pageItem);
  }

  onDeactivatePageForms(pageItem: PageItem | null): void {
    const pageItems: PageItem[] = this.baseMenuItems.concat(this.userMenuItems).concat(this.itemsInsideNode);
    for (const item of pageItems) {
      if (pageItem && item.id === pageItem?.id) {
        continue;
      }
      item.formIsActive = false;
    }
  }

  isBaseMenuItem(pageItem: PageItem): boolean {
    const alreadyExistingMenuItem = this.baseMenuItems.filter(m => m.id?.endsWith('_' + pageItem.id));
    return alreadyExistingMenuItem.length > 0;
  }

  onItemsInsideNodeChanged(map: Map<string, PageItem[]>): void {
    for (const key of map.keys()) {
      const value = map.get(key);
      if (value) {
        this.itemsInsideNodeMap.set(key, value);
      }
    }
    this.updatedItemsInsideNode.emit(this.itemsInsideNodeMap);
  }

  onGetItemsInsideNode(map: Map<string, PageItem[]>): void {
    for (const key of map.keys()) {
      const value = map.get(key);
      if (value) {
        this.itemsInsideNode = this.itemsInsideNode.concat(value);
        this.pagesInsideNode.emit(this.itemsInsideNode);
      }
    }
  }

  private useDragula() {
    this.configureDragulaForBaseMenuItems();
    this.configureDragulaForUserMenuItems();
  }

  private configureDragulaForBaseMenuItems(): void {
    this.subscription.add(this.dragulaService.drag(this.dragAndDropBaseMenuItems)
        .subscribe(({el}) => {
          el.classList.remove('ex-moved');
          this.dragAndDropUserMenuItemsInProgress = true;
        })
    );
    this.subscription.add(this.dragulaService.drop(this.dragAndDropBaseMenuItems)
        .subscribe(({el}) => {
          el.classList.add('ex-moved');
          this.baseMenuItemsOrderChanged.emit(this.baseMenuItems);
        })
    );
    this.subscription.add(this.dragulaService.over(this.dragAndDropBaseMenuItems)
        .subscribe(({el, container}) => {
          container.classList.add('ex-over');
        })
    );
    this.subscription.add(this.dragulaService.out(this.dragAndDropBaseMenuItems)
        .subscribe(({el, container}) => {
          container.classList.remove('ex-over');
        })
    );
  }

  private configureDragulaForUserMenuItems(): void {
    this.dragulaService.createGroup(this.dragAndDropUserMenuItems, {
      accepts: (el, target, source, sibling) => {
        try {
          const draggedElementIsNode: boolean = (el as any)?.classList?.contains(DesignService.THIS_IS_A_NODE_CLASS);
          if (draggedElementIsNode) {
            const targetIsSecondLevelContainer = (target as any)?.classList?.contains(DesignService.SECOND_LEVEL_CONTAINER);
            if (targetIsSecondLevelContainer) {
              return false;
            }
          }
          return true;
        } catch (ex) {
          return false;
        }
      }
    });

    this.subscription.add(this.dragulaService.drag(this.dragAndDropUserMenuItems)
        .subscribe(({el}) => {
          el.classList.remove('ex-moved');
          this.dragAndDropUserMenuItemsInProgress = true;
        })
    );
    this.subscription.add(this.dragulaService.drop(this.dragAndDropUserMenuItems)
        .subscribe(({el}) => {
          el.classList.add('ex-moved');
          this.dragAndDropUserMenuItemsInProgress = false;
          this.userMenuItemsOrderChanged.emit(this.userMenuItems);
        })
    );
    this.subscription.add(this.dragulaService.over(this.dragAndDropUserMenuItems)
        .subscribe(({el, container}) => {
          container.classList.add('ex-over');
        })
    );
    this.subscription.add(this.dragulaService.out(this.dragAndDropUserMenuItems)
        .subscribe(({el, container}) => {
          container.classList.remove('ex-over');
        })
    );
    this.subscription.add(this.dragulaService.dragend(this.dragAndDropUserMenuItems)
        .subscribe(() => {
          this.dragAndDropUserMenuItemsInProgress = false;
          this.userMenuItemsOrderChanged.emit(this.userMenuItems);
        })
    );
  }

}
